import clsx from 'clsx';
import React, {useState, useRef} from 'react';
import {Checkbox} from '@mui/material';
import {Icon, Button, Body} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import ElementInView from '@/components/element-in-view';
import styles from './ConsentFormScreen.module.scss';

const PRIVACY_POLICY_TABLE_URL =
  'https://static.lightricks.com/legal/privacy-policy.pdf#page=5';
const PRIVACY_POLICY_URL =
  'https://static.lightricks.com/legal/privacy-policy.pdf';
const PLATFORM_TERMS =
  'https://static.lightricks.com/legal/popular-pays-platform-terms-and-conditions.pdf';

export type ConsentFormScreenProps = {
  onAccept: () => void;
};

export const CONSENT_FORM_VERSION = '1.0.0';
function LegalText({onInView}: {onInView: () => void}) {
  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Welcome to Ltx Scaler by Lightricks™</h1>
      <p className={styles.paragraph}>
        At Ltx Scaler, we empower you to bridge the gap between imagination and
        creation with cutting-edge artificial intelligence. Lightricks is
        committed to developing generative AI responsibly, with creators at the
        center. Our dedication at Lightricks is to offer you a secure, safe, and
        responsible creative haven, while standing up for accountability,
        responsibility, and transparency in generative AI.
      </p>
      <h3 className={styles.lesser_title}>Our Commitment to You</h3>
      <ul className={styles.paragraph}>
        <li>
          Security: Your data is our priority. We use advanced security
          protocols to guard against unauthorized access and data breaches,
          ensuring your personal information remains protected.
        </li>
        <li>
          Safety: Creativity thrives in a safe space. We proactively monitor our
          platform to foster a community that values respect and constructive
          interaction.
        </li>
        <li>
          Responsibility: Ethical AI use is at the forefront of our mission. Our
          practices in AI development and deployment are designed to benefit our
          users and the broader society.
        </li>
        <li>
          Privacy: We handle your data with the utmost care. Our Privacy Policy
          outlines our transparent approach to data collection, usage, and
          protection.
        </li>
      </ul>
      <h3 className={styles.lesser_title}>Your Role in Our Community</h3>
      <p>
        While we do our part, the safety and integrity of Ltx Scaler also relies
        on your responsible use. By adhering to the following guidelines, you
        help ensure our community remains a productive and respectful space:
      </p>
      <ul className={styles.paragraph}>
        <li>
          Intellectual Property: Use only content that you’re authorized to
          share. Respecting IP rights of other users and third parties is vital
          for community trust and legal compliance.
        </li>
        <li>
          Illegal Content: Do not upload or create any content that is
          considered illegal pornographic material (including that of minors),
          graphic violence or gore, self-harm or promotion of terrorism or
          violent extremism.
        </li>
        <li>
          Defamation: Communicate with kindness. Avoid posting harmful or
          defamatory content that could harm reputations.
        </li>
        <li>
          Respect for Privacy: You must respect the privacy of others. This
          involves not sharing personal information without consent and not
          collecting or harvesting any data from Ltx Scaler that might violate
          privacy or data protection laws.
        </li>
        <li>
          Inclusivity: Discrimination has no place here. We stand for a
          community where everyone is welcome, regardless of race, gender,
          orientation, religion, or nationality.
        </li>
        <li>
          Legal Compliance: Abide by the law. Using our services legally is
          non-negotiable for community safety and integrity.
        </li>
      </ul>
      <h3 className={styles.lesser_title}>Your Privacy Preference</h3>
      <p className={styles.paragraph}>
        We are excited to offer you services that allow you to create and edit
        images, videos, and voice recordings using artificial intelligence
        (“AI”) technology. To do this, we create and store Face Models and Voice
        Models (as defined in our Privacy Policy) based on your content. Face
        and Voice Models may be deemed “biometric identifiers” according to the
        laws of your jurisdiction, but will not be used to identify you or any
        individual.
      </p>
      <p className={styles.paragraph}>
        Your use of services that utilize Face Models or Voice Models is
        optional and can be avoided by not uploading images, videos, or voice
        recordings. By checking the box below labeled “Accept the Privacy
        Preference” you agree to the creation, collection, storage, and use of
        Face Models and Voice Models in connection with your use of these
        services as explained in our{' '}
        <a target="_blank" href={PRIVACY_POLICY_TABLE_URL} rel="noreferrer">
          Privacy Policy – Table A
        </a>
        , which further details our retention policies and your rights.
      </p>
      <p className={styles.paragraph} style={{marginBottom: 0}}>
        Learn more in our{' '}
        <a target="_blank" href={PRIVACY_POLICY_URL} rel="noreferrer">
          Privacy Policy
        </a>
      </p>
      <div style={{position: 'relative', marginTop: 112}}>
        <ElementInView height={1} onInView={onInView} />
      </div>
    </div>
  );
}

export default function ConsentFormScreen({onAccept}: ConsentFormScreenProps) {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  const [isPrivacyPreferenceAccepted, setIsPrivacyPreferenceAccepted] =
    useState(false);
  const [isPrivacyPreferenceValid, setIsPrivacyPreferenceValid] =
    useState(true);

  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [isTermsValid, setIsTermsValid] = useState(true);

  const handleAccept = () => {
    setIsPrivacyPreferenceValid(isPrivacyPreferenceAccepted);
    setIsTermsValid(isTermsAccepted);

    if (isPrivacyPreferenceAccepted && isTermsAccepted) {
      onAccept();
    }
  };

  const legalTextRef = useRef<HTMLDivElement>(null);

  return (
    <div className={styles.consentFormScreenContainer}>
      <div className={styles.form_box}>
        <div
          className={styles.text}
          ref={legalTextRef}
          data-testid="consent-form-legal-text"
        >
          <LegalText onInView={() => setHasScrolledToBottom(true)} />
        </div>
        <div className={styles.interactions}>
          {!hasScrolledToBottom ? (
            <div className={clsx(styles.label, styles.scroll_suggestion)}>
              <Icon
                size="small"
                appearance="neutral"
                name="Actions-Arrow-Down-Small"
              />
              Scroll to the end to activate accept terms
              <Icon
                size="small"
                appearance="neutral"
                name="Actions-Arrow-Down-Small"
              />
            </div>
          ) : (
            <>
              <div className={styles.row}>
                <div className={styles.checkbox_container}>
                  <div className={styles.checkbox_row}>
                    <Checkbox
                      checked={isPrivacyPreferenceAccepted}
                      onChange={() =>
                        setIsPrivacyPreferenceAccepted((state) => !state)
                      }
                      id="privacy-preference"
                    />
                    <label
                      className={styles.label}
                      htmlFor="privacy-preference"
                    >
                      *Accept our Privacy Preference
                    </label>
                  </div>
                  {!isPrivacyPreferenceValid ? (
                    <Body size="sm" className={styles.required_field}>
                      This field is required
                    </Body>
                  ) : null}
                </div>
              </div>
              <div className={styles.row}>
                <div className={styles.checkbox_container}>
                  <div className={styles.checkbox_row}>
                    <Checkbox
                      checked={isTermsAccepted}
                      onChange={() => setIsTermsAccepted((state) => !state)}
                      id="terms"
                    />
                    <label className={styles.label} htmlFor="terms">
                      *Accept and acknowledge our{' '}
                      <a
                        target="_blank"
                        className={styles.link}
                        href={PLATFORM_TERMS}
                        rel="noreferrer"
                      >
                        Platform Terms and Conditions
                      </a>{' '}
                      and{' '}
                      <a
                        target="_blank"
                        className={styles.link}
                        href={PRIVACY_POLICY_URL}
                        rel="noreferrer"
                      >
                        Privacy Policy
                      </a>
                      , and uphold the standard of responsibility use as
                      outlined
                    </label>
                  </div>
                  {!isTermsValid ? (
                    <Body size="sm" className={styles.required_field}>
                      This field is required
                    </Body>
                  ) : null}
                </div>
              </div>
            </>
          )}
          <div className={styles.button_row}>
            <Button
              appearance="neutral"
              mode="filled"
              size="small"
              className={styles.accept_button}
              disabled={
                !hasScrolledToBottom ||
                !isPrivacyPreferenceAccepted ||
                !isTermsAccepted
              }
              onClick={handleAccept}
            >
              {translate('Accept')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
